// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef, useState } from 'react';

import type { PendingChoice } from '../../kortlaegning/cellDraft';

/**
 * A select/checkbox/toggle's choice while its write is in flight. Each choice
 * gets a token; only the latest one's settling clears it, so a quick second
 * choice is not cleared by the first one's response. Shared by the table's
 * `ResourceCell` and the Bygning inspector's Ja/Nej and tag toggles, so both
 * judge the next press against the pending choice (b48394a).
 */
export function usePendingChoice(onCommit: (value: string | null) => void | Promise<void>) {
  const [pending, setPending] = useState<PendingChoice | undefined>(undefined);
  const token = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  function choose(value: string | null) {
    const mine = ++token.current;
    setPending({ value });
    void Promise.resolve(onCommit(value)).finally(() => {
      if (mounted.current && token.current === mine) setPending(undefined);
    });
  }
  return { pending, choose };
}
